import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Search, Loader2, MapPin } from "lucide-react";

/**
 * AddressAutocomplete — combobox backed by /api/geocode/autocomplete
 * (proxied to OpenStreetMap Nominatim). US-only.
 *
 * Props:
 *  - value:   current text in the input (address1)
 *  - onChange(text): fired on every keystroke so parent state stays in sync
 *  - onSelect({address1, city, state, postal, country, label}): fired when
 *    the user picks a suggestion — parent should fill the other fields.
 */
export default function AddressAutocomplete({ value, onChange, onSelect, placeholder = "Street address" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const debounceRef = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    // Close on outside click
    const handler = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const query = (q) => {
    clearTimeout(debounceRef.current);
    if (!q || q.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/geocode/autocomplete", {
          params: { q, country: "us", limit: 6 },
        });
        setSuggestions(data || []);
        setOpen(true);
        setActive(-1);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    query(v);
  };

  const pick = (s) => {
    onSelect({
      address1: s.address1 || value,
      city: s.city || "",
      state: s.state || "",
      postal: s.postal || "",
      country: s.country || "US",
      label: s.label,
    });
    setOpen(false);
    setSuggestions([]);
  };

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, suggestions.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); pick(suggestions[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  };

  return (
    <div ref={boxRef} className="relative">
      <div className="relative">
        <Search strokeWidth={1.25} className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          type="text"
          value={value}
          onChange={handleChange}
          onKeyDown={onKeyDown}
          onFocus={() => value.length >= 3 && suggestions.length && setOpen(true)}
          placeholder={placeholder}
          className="w-full border border-border p-4 pl-10 pr-10 focus:border-navy outline-none"
          data-testid="checkout-address1"
          autoComplete="off"
        />
        {loading && <Loader2 strokeWidth={1.25} className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground animate-spin" />}
      </div>

      {open && suggestions.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-20 mt-1 bg-white border border-navy max-h-72 overflow-auto shadow-lg" data-testid="address-suggestions">
          {suggestions.map((s, i) => (
            <li key={i}>
              <button
                type="button"
                onClick={() => pick(s)}
                onMouseEnter={() => setActive(i)}
                className={`w-full text-left px-4 py-3 flex items-start gap-3 transition-colors ${active === i ? "bg-navy text-cream" : "hover:bg-muted"}`}
                data-testid={`address-suggestion-${i}`}
              >
                <MapPin strokeWidth={1.25} className={`h-4 w-4 mt-0.5 flex-shrink-0 ${active === i ? "text-cream" : "text-navy"}`} />
                <div className="flex-1 min-w-0">
                  <div className={`text-sm font-medium truncate ${active === i ? "text-cream" : ""}`}>
                    {s.address1 || s.label?.split(",")[0]}
                  </div>
                  <div className={`text-xs truncate ${active === i ? "text-cream/70" : "text-muted-foreground"}`}>
                    {[s.city, s.state, s.postal].filter(Boolean).join(", ")}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
